import { sortBy } from 'lodash'
import { createSelector } from 'reselect'

import { getDateValue } from '../helpers/file'

// Other selector dependencies
import { isLoggedInSelector } from './clientFirstOrder'
import { cloudFileListSelector, fileSystemKnownFilesSelector } from './knownFilesFirstOrder'
import { shouldBeInProSelector } from './secondOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').KnownFilesEntry} KnownFilesEntry
 */

/**
 * @param {Array<KnownFilesEntry>} filesArray
 * @return {Record<number, KnownFilesEntry>}
 */
const filesByPosition = (filesArray) => {
  /**
   * @type {Record<number, KnownFilesEntry>}
   */
  const byPosition = {}
  filesArray.forEach((file, index) => {
    byPosition[index + 1] = file
  })
  return byPosition
}
/**
 * @param {Array<KnownFilesEntry>} filesArray
 * @return {Record<number, KnownFilesEntry>}
 */
const filesById = (filesArray) => {
  /**
   * @type {Record<String, KnownFilesEntry>}
   */
  const byId = {}
  filesArray.forEach((file, _index) => {
    if (file.id) {
      byId[file.id] = file
    }
  })
  return byId
}

/**
 * @param {PlottrState} _state
 * @param {String} searchTerm
 * @return {String}
 */
const searchTermSelector = (_state, searchTerm) => searchTerm
// FIXME(ed): it looks like the calls to `filesByPosition` and
// `filesById` are backwards.  I think `filesById` is only valid for
// Pro files because local files don't have ids.
/**
 * @type {function(PlottrState, String): ([Array<number>, Record<number, KnownFilesEntry>] | [Array<String>, Record<String, KnownFilesEntry>])}
 */
export const sortedKnownFilesSelector = createSelector(
  fileSystemKnownFilesSelector,
  cloudFileListSelector,
  shouldBeInProSelector,
  isLoggedInSelector,
  searchTermSelector,
  (fileSystemFiles, cloudFiles, shouldBeInPro, isLoggedIn, searchTerm) => {
    if (shouldBeInPro && !isLoggedIn) {
      return [[], {}]
    }
    const files = shouldBeInPro ? filesByPosition(cloudFiles) : filesById(fileSystemFiles)
    const filteredFileIds = Object.keys(files).filter((id) => {
      if (searchTerm && searchTerm.length > 1) {
        const f = files[`${id}`]
        return (f.fileName || f.path).toLowerCase().includes(searchTerm.toLowerCase())
      } else {
        return true
      }
    })
    const sortedIds = sortBy(filteredFileIds, (id) => getDateValue(files[`${id}`])).reverse()

    return [sortedIds, files]
  }
)

/**
 * @type {function(PlottrState, String | Null): Array<KnownFilesEntry>}
 */
export const flatSortedKnownFilesSelector = createSelector(
  fileSystemKnownFilesSelector,
  cloudFileListSelector,
  shouldBeInProSelector,
  isLoggedInSelector,
  searchTermSelector,
  (fileSystemFiles, cloudFiles, shouldBeInPro, isLoggedIn, searchTerm) => {
    if (shouldBeInPro && !isLoggedIn) {
      return []
    }
    const files = shouldBeInPro ? cloudFiles : fileSystemFiles
    const filteredFiles = files.filter((file) => {
      if (searchTerm && searchTerm.length > 1) {
        return file.fileName.toLowerCase().includes(searchTerm.toLowerCase())
      } else {
        return true
      }
    })
    const sorted = sortBy(filteredFiles, (file) => getDateValue(file)).reverse()

    return sorted
  }
)
